import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Grid,
    Paper
} from '@mui/material';
import { useSelector } from 'react-redux';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import TEXT from './text';
import * as calculationService from './services/calculationService';

const DailyGrowthChart = ({ groupId, storeName }) => {
    const language = useSelector(state => state.App_Settings.language);
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [growthData, setGrowthData] = useState(null);

    useEffect(() => {
        loadGrowthData();
    }, [groupId]);

    const loadGrowthData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const data = await calculationService.getDashboardData(
                groupId,
                'daily_growth',
                { 
                    storeName,
                    days: 30,
                    dateField: 'created_at'
                }
            );
            
            setGrowthData(data);
        } catch (err) {
            console.error('[DailyGrowth] Error loading data:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const date = new Date(label);
            return (
                <Box sx={{ bgcolor: 'background.paper', p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2">{date.toLocaleDateString()}</Typography>
                    <Typography variant="body2" color="primary">
                        {TEXT.NewRecords[language]}: {payload[0].value}
                    </Typography>
                </Box>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: { xs: 180, sm: 220, md: 300 } }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!growthData || growthData.daily_data.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: { xs: 180, sm: 220, md: 300 } }}>
                <Typography color="text.secondary">
                    {TEXT.NoData[language]}
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Typography variant={{ xs: 'subtitle1', sm: 'h6' }} sx={{ mb: { xs: 1, sm: 2 } }}>
                {TEXT.DailyGrowth[language]}
            </Typography>
            
            {/* Summary Statistics */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {TEXT.Total[language]}
                        </Typography>
                        <Typography variant="h6">
                            {growthData.summary.total.toLocaleString()}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {TEXT.Average[language]}
                        </Typography>
                        <Typography variant="h6">
                            {growthData.summary.average}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {TEXT.Peak[language]}
                        </Typography>
                        <Typography variant="h6">
                            {growthData.summary.peak}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Days
                        </Typography>
                        <Typography variant="h6">
                            {growthData.summary.days}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>

            {/* Line Chart */}
            <Box sx={{ 
                height: { xs: 160, sm: 200, md: 250 },
                width: '100%'
            }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={growthData.daily_data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                            dataKey="date" 
                            tickFormatter={(date) => {
                                const d = new Date(date);
                                return `${d.getMonth() + 1}/${d.getDate()}`;
                            }}
                        />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Line 
                            type="monotone" 
                            dataKey="count" 
                            stroke="#8884d8" 
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};

export default DailyGrowthChart;
